import { useMemo } from "react";

import {
  File as FileIcon,
  Upload as UploadIcon,
  ArrowUpRight,
} from "@hl/base-components/lib/assets/icons.generated";
import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import {
  UploadStatus,
  useUploadManager,
} from "@hl/base-components/lib/upload/context";
import {
  FEATURE_FLAGS,
  useFeatureFlags,
} from "@hl/shared-features/lib/features/auth/hooks/useFeatureFlags";
import { Anchor, Box, Button, Group, Stack, Text, Title } from "@mantine/core";
import { FileWithPath } from "@mantine/dropzone";
import { IconDownload } from "@tabler/icons";
import { BlobReader, Entry, TextWriter, ZipReader } from "@zip.js/zip.js";
import { isEmpty } from "lodash";
import { ErrorCode, FileError, FileRejection } from "react-dropzone";
import { TEXT_COLOR } from "~/../../base-components/lib/theme/colors";

import { DraftProgress } from "apollo/graphql.generated";
import { IN_DEVELOPMENT, SUPPORT_URL, TOOLS_CDN_URL } from "config";
import SaveDraftButton from "~/features/collections/forms/CreateCollectionMultistepForm/SaveDraftButton";
import {
  SeriesTokenMetadata,
  useCreateCollectionFormContext,
  useCreateCollectionState,
} from "~/features/collections/forms/CreateCollectionMultistepForm/context";
import useSaveDraft from "~/features/collections/forms/CreateCollectionMultistepForm/useSaveDraft";

import FormTitle from "./FormTitle";
import HLStudioLink from "./HLStudioLink";
import NextStepButton from "./NextStepButton";
import SeriesMetadataTablePreview from "./SeriesMetadataTablePreview";
import ZipUpload from "./ZipUpload";

const isStrictlyIncreasingByOneSequence = (
  arr: number[],
  startsWith: number
): boolean => {
  if (arr.length < 2) {
    return true;
  }
  if (arr[0] !== startsWith) {
    return false;
  }
  for (let i = 1; i < arr.length; i++) {
    if (arr[i] !== arr[i - 1] + 1) {
      return false;
    }
  }
  return true;
};

const MAX_SIZE_MB = 3500;
const MAX_SIZE_GB = MAX_SIZE_MB / 1000;
//20GB
const INCREASED_MAX_SIZE_MB = 20000;
const INCREASED_MAX_SIZE_GB = INCREASED_MAX_SIZE_MB / 1000;

const UploadCollectionAndMetadata = () => {
  const skipValidation = useFeatureFlags(
    FEATURE_FLAGS.SKIP_SERIES_ZIP_VALIDATION
  );
  const showZipPreviewFlag = useFeatureFlags(
    FEATURE_FLAGS.SHOW_SERIES_UPLOAD_ZIP_PREVIEW
  );
  const increaseZipLimit = useFeatureFlags(
    FEATURE_FLAGS.ZIP_INCREASE_FILE_SIZE_LIMIT
  );

  const {
    dispatch,
    state: {
      series: { newTokens, uploadedTokens },
    },
  } = useCreateCollectionState();
  const { state, dispatch: uploadDispatch } = useUploadManager();

  const tokens = useMemo(
    () => (newTokens.length ? newTokens : uploadedTokens || []),
    [newTokens, uploadedTokens]
  );

  const form = useCreateCollectionFormContext();

  const showZipPreview =
    showZipPreviewFlag &&
    (![UploadStatus.NOT_STARTED, UploadStatus.ERROR].includes(state.status) ||
      tokens.length);

  const onMediaChanged = (url: string | null) => {
    dispatch({
      type: "UPDATE_SERIES",
      payload: {
        newMediaUrl: url,
      },
    });
  };

  const { saveDraft, loading } = useSaveDraft({
    furthestStep: DraftProgress.ADDED_METADATA,
  });

  const onNextStep = () => {
    dispatch({
      type: "NEXT_STEP",
      payload: {
        form,
      },
    });
  };

  const zipFileValidation = async (
    files: FileWithPath[]
  ): Promise<FileRejection[]> => {
    // ZipUpload "multiple" option is false by default
    const file = files[0];

    const errors: FileError[] = [];
    let hasMetadataJson = false;
    let hasBadTokenFilenameError = false;
    const images = new Set<string>();
    const usedImages = new Set<string>();
    const animations = new Set<string>();
    const usedAnimations = new Set<string>();
    const metadataIds = new Set<number>();
    let metadata = {};

    const validAnimationFilename = /^(animations)\/[\w\.\-]+.(mp4|webm)/;
    const validImageFilename = /^(images)\/[\w\.\-]+.(png|jpeg|jpg|gif|webp)/;

    const sizeAllowed = increaseZipLimit
      ? INCREASED_MAX_SIZE_MB * 1000000
      : MAX_SIZE_MB * 1000000;
    if (file.size > sizeAllowed) {
      errors.push({
        code: ErrorCode.FileTooLarge,
        message: `zip file cannot be greater than ${
          increaseZipLimit ? INCREASED_MAX_SIZE_GB : MAX_SIZE_GB
        }GB.`,
      });
    }

    const zipReader = new ZipReader(new BlobReader(file));
    const entries = (await zipReader.getEntries()).filter(
      (entry) => !entry.filename.match(/^__MACOSX\/|\.DS_Store/)
    );

    /*
     * expect format to be:
     *   - metadata.json
     *   - images/image1.png
     *   - images/image12.png
     *   ...
     *   - animations/animation1.webm
     *   - animations/animation2.mp4
     *   ...
     */
    for (const entry of entries) {
      if (
        entry.directory &&
        entry.filename !== "images/" &&
        entry.filename !== "animations/"
      ) {
        errors.push({
          code: ErrorCode.FileInvalidType,
          message: `zip file has an unexpected folder: ${entry.filename}`,
        });
      } else if (!entry.directory && entry.filename === "metadata.json") {
        hasMetadataJson = true;
        const metadataContent = await entry.getData(new TextWriter());
        try {
          metadata = JSON.parse(metadataContent);
        } catch (err) {
          errors.push({
            code: ErrorCode.FileInvalidType,
            message: "Invalid metadata.json file format.",
          });
        }
      } else if (!entry.directory) {
        const filepath = entry.filename;
        const filename = entry.filename.split("/").pop() ?? "";
        const validAnimation = filepath.match(validAnimationFilename);
        const validImage = filepath.match(validImageFilename);

        // in images + animations dir
        if (!validAnimation && !validImage) {
          if (!hasBadTokenFilenameError) {
            hasBadTokenFilenameError = true;
            errors.push({
              code: ErrorCode.FileInvalidType,
              message: `bad token name(s) - expect format like image1.png (or valid file-extension)`,
            });
          }
          errors.push({
            code: ErrorCode.FileInvalidType,
            message: `zip file has token media with bad filename: ${filepath}`,
          });
        } else if (validAnimation) {
          animations.add(filename);
        } else if (validImage) {
          images.add(filename);
        }
      }
    }

    await zipReader.close();
    // do metadata json validation last, since need to have parsed all images + animations
    if (!hasMetadataJson) {
      errors.unshift({
        code: ErrorCode.FileInvalidType,
        message: "zip file does not have a metadata.json file",
      });
    } else if (typeof metadata !== "object" || !Array.isArray(metadata)) {
      errors.unshift({
        code: ErrorCode.FileInvalidType,
        message: "Invalid metadata.json file format.",
      });
    } else if (typeof metadata === "object" && Array.isArray(metadata)) {
      for (const [i, obj] of metadata.entries()) {
        // console.log("DEBUG 003 - ", { i, obj });
        const keys = Object.keys(obj);
        if (!keys.includes("metadataId")) {
          errors.unshift({
            code: ErrorCode.FileInvalidType,
            message: `metadata '${i}' is missing the metadataId field`,
          });
        } else {
          metadataIds.add(parseInt(obj["metadataId"], 10));
        }

        if (keys.includes("imageRef")) {
          if (!images.has(obj["imageRef"])) {
            errors.unshift({
              code: ErrorCode.FileInvalidType,
              message: `metadata has image '${obj["imageRef"]}', but zip is missing that file`,
            });
          } else {
            usedImages.add(obj["imageRef"]);
          }
        }
        if (keys.includes("animationRef")) {
          if (!animations.has(obj["animationRef"])) {
            errors.unshift({
              code: ErrorCode.FileInvalidType,
              message: `metadata has animation '${obj["animationRef"]}', but zip is missing that file`,
            });
          } else {
            usedAnimations.add(obj["animationRef"]);
          }
        }
      }

      if (images.size != usedImages.size) {
        const difference = new Set(
          [...images].filter((image) => !usedImages.has(image))
        );
        errors.unshift({
          code: ErrorCode.FileInvalidType,
          message:
            `mismatch between images uploaded and images used in metadata references` +
            (difference.size > 0
              ? `, unused images: ${Array.from(difference).join(", ")}`
              : ""),
        });
      }
      if (animations.size != usedAnimations.size) {
        const difference = new Set(
          [...animations].filter((animation) => !usedAnimations.has(animation))
        );
        errors.unshift({
          code: ErrorCode.FileInvalidType,
          message:
            `mismatch between animations uploaded and animations used in metadata references` +
            (difference.size > 0
              ? `, unused animations: ${Array.from(difference).join(", ")}`
              : ""),
        });
      }

      // validate metadataIds are monotonically increasing sequence (even unsorted)
      if (!isStrictlyIncreasingByOneSequence(Array.from(metadataIds), 1)) {
        errors.unshift({
          code: ErrorCode.FileInvalidType,
          message: `metadataIds in metadata entries are numbered wrong, must start with '1' and increase by one each time`,
        });
      }
    }

    IN_DEVELOPMENT &&
      console.log("validate zip file", {
        errors,
        hasMetadataJson,
        metadata,
      });
    if (isEmpty(errors) || skipValidation) {
      dispatch({
        type: "UPDATE_SERIES",
        payload: {
          newTokenImageCount: metadataIds.size, // TODO: rename newTokenImageCount to tokenCount
        },
      });
      return [];
    } else {
      return [
        {
          file: files[0],
          errors,
        },
      ];
    }
  };

  const handleEntries = async (entries: Entry[]) => {
    let metadata: SeriesTokenMetadata[] | undefined = undefined;
    const imageMap = new Map<string, Entry>();
    for (const entry of entries) {
      if (entry.filename.endsWith(".json")) {
        metadata = JSON.parse(await entry.getData(new TextWriter()));
      } else {
        const paths = entry.filename.split("/");
        imageMap.set(paths[paths.length - 1], entry);
      }
    }

    const tokens = metadata?.map((token) => ({
      ...token,
      imageEntry: !!token["imageRef"]
        ? imageMap.get(token["imageRef"] as string)
        : undefined,
    }));

    dispatch({
      type: "UPDATE_SERIES",
      payload: {
        newTokens: tokens ?? [],
      },
    });
  };

  return (
    <div>
      <FormTitle
        title="Upload series"
        buttons={[
          <SaveDraftButton
            key="draft"
            onClick={() => saveDraft()}
            loading={loading}
          />,
          <NextStepButton key="next" onClick={onNextStep} loading={loading} />,
        ]}
      />
      <Stack spacing={48} mt={35}>
        <Stack>
          <ZipUpload
            validate={zipFileValidation}
            handleEntries={handleEntries}
            mediaError={(form.errors["metadata.mediaUrl"] as string) ?? ""}
            onMediaChanged={onMediaChanged}
          />
          {showZipPreview ? (
            <Stack spacing={20}>
              <Group position="apart">
                <Title order={6} fw={500}>
                  Review your uploaded collection
                </Title>
                <Button
                  color={SECONDARY_COLOR}
                  onClick={() => {
                    uploadDispatch({
                      type: "UPLOAD_RESET",
                    });
                  }}
                  px={12}
                  leftIcon={<UploadIcon width={16} />}
                >
                  Upload new .zip
                </Button>
              </Group>
              <SeriesMetadataTablePreview tokens={tokens} />
            </Stack>
          ) : (
            <></>
          )}
        </Stack>
      </Stack>
      <Group mt={40} position="apart">
        <Text size={25} weight={500}>
          Instructions
        </Text>
        <Group spacing={8}>
          <Anchor
            c="button"
            type="button"
            href={`${TOOLS_CDN_URL}/files/highlight-example-project.zip`}
            download={"highlight-example-project.zip"}
          >
            <Button
              size="lg"
              px={16}
              color={SECONDARY_COLOR}
              leftIcon={<IconDownload />}
            >
              Download example .zip
            </Button>
          </Anchor>
          <Anchor
            c="button"
            target="_blank"
            rel="noreferrer"
            href={SUPPORT_URL.SERIES_LEARN_MORE}
            type="button"
          >
            <Button
              size="lg"
              pl={16}
              pr={10}
              color={SECONDARY_COLOR}
              leftIcon={<FileIcon />}
              rightIcon={<ArrowUpRight width={80} height={80} />}
            >
              Read the full docs
            </Button>
          </Anchor>
        </Group>
      </Group>
      <Text size="sm" component="p">
        Upload a .zip file that contains your series. The .zip file should be
        3.5GB or less. It should contain: 
      </Text>
      <Text size="sm" weight={500}>
        1. A folder named &quot;images&quot;
      </Text>
      <Text size="sm" component="p">
        This folder contains any image assets used by your tokens. We currently
        support .png, .jpeg, .jpg, .gif, or .webp files for images.
      </Text>
      <Text size="sm" weight={500}>
        2. A folder named &quot;animations&quot; (only necessary if your series
        uses .mp4 or .webm files for animations)
      </Text>
      <Text size="sm" component="p">
        This folder contains any animation assets used by your tokens. We
        currently support .mp4 and .webm files for animations.
      </Text>
      <Text size="sm" weight={500}>
        3. A .json file named &quot;metadata.json&quot;
      </Text>
      <Box py={"md"}>
        <HLStudioLink
          link={"https://studio.highlight.xyz/?url=/&tab=series-metadata"}
          title="Create metadata.json with Highlight Studio"
          text="Use our tool to create metadata.json files for your series. It offers validation and auto-generation of metadata files."
        />
      </Box>
      <Text size="sm" component="p">
        The metadata.json file should contain an array of objects, where each
        object represents a token. Each token object should contain the
        following entries:
      </Text>
      <Text size="sm">
        <ul>
          <li>
            <Text size="sm" weight={500} span>
              &quot;metadataId&quot;
            </Text>
            : An integer that identifies this token. Metadata IDs must start at
            1 and be sequential. Note that a token with a given metadata ID may
            have a different token ID when it is minted if you have a random
            mint mechanic in place (see the price and distribution step for
            details).
          </li>
          <li>
            <Text size="sm" weight={500} span>
              &quot;imageRef&quot;
            </Text>
            : The name of the image asset to use for the token (for example:
            &quot;image-1.png&quot;)
          </li>
          <li>
            <Text size="sm" weight={500} span>
              &quot;animationRef&quot;
            </Text>
            : The name of the animation asset to use for the token (for example:
            &quot;animation-1.mp4&quot;). This entry is optional and only
            necessary if you want to use a separate animation asset.
          </li>
          <li>
            <Text size="sm" weight={500} span>
              &quot;name&quot;
            </Text>
            : The name of the token. This will appear as the token’s name on
            marketplaces.
          </li>
          <li>
            <Text size="sm" weight={500} span>
              &quot;description&quot;
            </Text>
            : The description of the token. This will appear as the token’s
            description on marketplaces, and is distinct from your
            collection&apos;s description (specified in the collection details
            step).
          </li>
        </ul>
      </Text>
      <Text size="sm" component="p">
        You may add additional entries that specify any custom traits for this
        token, for example:
      </Text>
      <Text size="sm">
        <ul>
          <li>&quot;Background Color&quot;: &quot;Red&quot;</li>
          <li>&quot;Height&quot;: 7.2</li>
          <li>&quot;Shiny&quot;: false</li>
        </ul>
      </Text>
      <Text size="sm" component="p">
        If you’re not sure how to create a .json file, you can easily convert a
        .csv spreadsheet (which you can export from Excel, Google Sheets, etc.)
        to .json format using{" "}
        <Anchor
          c="inherit"
          weight={500}
          href={SUPPORT_URL.JSON_CSV_CONVERTER}
          target="_blank"
          rel="noreferrer"
        >
          this tool
        </Anchor>
        .
      </Text>
      <Text size="sm" component="p">
        To ensure that your files are in the correct formats, see the examples
        below,{" "}
        <Anchor
          c="inherit"
          weight={500}
          href={`${TOOLS_CDN_URL}/files/highlight-example-project.zip`}
          download={"highlight-example-project.zip"}
        >
          download the example .zip
        </Anchor>
        ,{" "}
        <Anchor
          c="inherit"
          weight={500}
          href={SUPPORT_URL.SERIES_LEARN_MORE}
          target="_blank"
          rel="noreferrer"
        >
          or read the full documentation
        </Anchor>
        .
      </Text>
      <Text size="sm" component="p" mb={40}>
        Please zip by selecting the images folder, animations folder (if
        relevant), and metadata.json file themselves, not a folder containing
        them.
      </Text>
      <video
        style={{ display: "block", width: "100%", borderRadius: 10 }}
        autoPlay
        loop
        src="https://highlight-creator-assets.highlight.xyz/platform-assets/zip-instructions.mp4"
      ></video>
      <Text size="sm" mt={5} color={TEXT_COLOR.SECONDARY}>
        Zip the contents together, do not zip a folder
      </Text>
      <NextStepButton
        size="xl"
        onClick={onNextStep}
        loading={loading}
        fullWidth={true}
        mt={40}
      />
    </div>
  );
};

export default UploadCollectionAndMetadata;
